<template>
  <div id="container">
    <label-dialog ref="graphLabelDialog" />
    <error-message ref="errorDialog" />
    <v-container class="pt-0">
      <color-palette
        @color-select="reflectColor"
        @color-init="initColor"
        :colorId="colorId"
      ></color-palette>
      <v-layout row wrap>
        <v-flex xs4>
          <v-btn
            v-if="mode == 'lines'"
            small
            color="primary"
            class="black--text"
            @click="writingStraightLine"
            >直線</v-btn
          >
          <v-btn
            v-else
            small
            color="primary lighten-3"
            class="grey--text"
            @click="writingStraightLine"
            >直線</v-btn
          >
        </v-flex>
        <v-flex xs4>
          <v-btn
            v-if="mode == 'bezier'"
            small
            color="primary"
            class="black--text"
            @click="writingBezierCurve"
            >曲線</v-btn
          >
          <v-btn
            v-else
            small
            color="primary lighten-3"
            class="grey--text"
            @click="writingBezierCurve"
            >曲線</v-btn
          >
        </v-flex>
        <v-flex xs4>
          <v-btn small @click="onResetButtonClickHandler">最初から</v-btn>
        </v-flex>
        <v-flex xs6>
          <v-btn
            v-if="flgLockGraph"
            id="scroll-lock-btn"
            dark
            color="secondary"
            @click="scrollLock(true)"
            >グラフ編集モードにする</v-btn
          >
          <v-btn
            v-else
            id="scroll-lock-btn"
            flat
            dark
            color="secondary lighten-2"
            @click="scrollLock(false)"
            >グラフ編集モードを終了する</v-btn
          >
        </v-flex>
      </v-layout>
    </v-container>
    <div>
      <svg
        id="svgGraph"
        @touchstart="onSvgTouchStartHandler"
        @touchmove="onSvgTouchMoveHandler"
        @touchend="onSvgTouchEndHandler"
        @mousedown="onSvgMouseDownHandler"
        @mousemove="onSvgMouseMoveHandler"
        @mouseup="onSvgMouseUpHandler"
        :width="size"
        :height="size"
        :style="svgStyles()"
        preserveAspectRatio="none"
        :viewBox="`${viewboxX} ${viewboxY} ${viewboxW} ${viewboxH}`"
        version="1.1"
        xmlns="http://www.w3.org/2000/svg"
      >
        <!--paddingの可視化: 頂点の丸はグラフ描画領域境界に打った場合はみ出すため、paddingがないと欠ける。-->
        <rect
          class="svg_padding"
          style="fill: transparent;"
          x="0"
          y="0"
          :width="padding"
          :height="size * magnify"
        />
        <rect
          class="svg_padding"
          style="fill: transparent;"
          x="0"
          y="0"
          :width="size * magnify"
          :height="padding"
        />
        <rect
          class="svg_padding"
          style="fill: transparent;"
          :x="size * magnify - padding"
          y="0"
          :width="padding"
          :height="size * magnify"
        />
        <rect
          class="svg_padding"
          style="fill: transparent;"
          x="0"
          :y="size * magnify - padding"
          :width="size * magnify"
          :height="padding"
        />
        <!--/padding-->

        <!-- 縦ラベル用の描画範囲 -->
        <rect
          class="svg_labelArea"
          style="fill: transparent;"
          :x="padding"
          :y="padding"
          :width="labelWidth"
          :height="innerSize * magnify - labelHeight"
          @touchmove="onLabelTouchMoveHandler"
          @touchend="onLeftLabelTouchEndHandler"
          @mouseup="onLeftLabelMouseUpHandler"
        />
        <!-- 横ラベル用の描画範囲 -->
        <rect
          class="svg_labelArea"
          style="fill: transparent;"
          :x="padding + labelWidth"
          :y="padding + (innerSize * magnify - labelHeight)"
          :width="innerSize * magnify - labelWidth"
          :height="labelHeight"
          @touchmove="onLabelTouchMoveHandler"
          @touchend="onBottomLabelTouchEndHandler"
          @mouseup="onBottomLabelMouseUpHandler"
        />

        <!--グラフ描画範囲(ラベル表示部分を除いたもの)-->
        <rect
          class="svg_graphArea"
          :style="drawAreaRectStyles()"
          :x="padding + labelWidth"
          :y="padding"
          :width="innerSize * magnify - labelWidth"
          :height="innerSize * magnify - labelHeight"
        />

        <!--グラフ-->
        <path
          class="svg_graphPath"
          :x="padding + labelWidth"
          :y="padding"
          :style="graphPathStyles()"
          :d="graphPath"
        />

        <!--操作中に表示するグラフ-->
        <path
          class="svg_ghostGraphPath"
          :style="ghostGraphPathStyles()"
          :d="ghostGraphPath"
        />

        <!--塗りつぶしの基準線-->
        <path
          class="svg_waterlinePath"
          :style="waterlinePathStyles()"
          :d="waterlinePath"
        />

        <!--副目盛り-->
        <path
          class="svg_axisLinePath"
          v-for="axis in axisDataList"
          :style="xAxisPathStyles(axis)"
          :key="`xAxis_${axis.x}_${axis.y}`"
          :d="axis.xPath"
        />
        <path
          class="svg_degreeLinePath"
          v-for="degree in degreeDataList"
          :style="degreePathStyles(degree)"
          :key="`degree_${degree.y}`"
          :d="degree.path"
        />

        <!--点-->
        <circle
          class="svg_vertexCircle"
          v-for="data in dataList"
          :style="pointCircleStyles()"
          :key="`point_${data.x}`"
          :cx="data.x"
          :cy="data.y"
          r="5"
        />
        <circle
          class="svg_ghostVertexCircle"
          v-for="ghostData in ghostDataList"
          :style="pointCircleStyles()"
          :key="`ghostPoint_${ghostData.x}`"
          :cx="ghostData.x"
          :cy="ghostData.y"
          r="5"
        />

        <!--横軸のラベル-->
        <text
          class="svg_axisLabelText"
          v-for="data in axisDataList"
          :style="textLabelStyles('axis', data)"
          :key="`xLabel_${data.x}_${data.y}`"
          :x="data.x"
          :y="size * magnify - labelHeight + drawAreaMargin"
          @touchmove.stop="onBottomAxisLabelTouchMoveHandler"
          @touchend.stop="
            onBottomAxisLabelTouchEndtHandler({
              x: data.x,
              y: data.y,
              axis: 'x'
            })
          "
          @mouseup.stop="
            onBottomAxisLabelTouchEndtHandler({
              x: data.x,
              y: data.y,
              axis: 'x'
            })
          "
        >
          {{ data.xLabel || "ラベル追加" }}
        </text>

        <!--縦軸のラベル-->
        <text
          class="svg_degreeLabelText"
          v-for="degree in degreeDataList"
          :style="textLabelStyles('degree', degree)"
          :key="`yLabel_${degree.y}`"
          :x="padding"
          :y="degree.y"
          @touchend.stop="onLeftDegreeLabelTouchEndHandler(degree.y)"
          @mouseup.stop="onLeftDegreeLabelTouchEndHandler(degree.y)"
        >
          {{ degree.label || "タップしてラベル追加" }}
        </text>
      </svg>
    </div>

    <modal-for-create-graph
      :tags="graph_tags"
      :graphId="graph_id"
      :title="graph_title"
      :drawType="drawType"
      :baseGraphId="base_graph_id"
      :secretFlg="secretFlg"
      ref="modalForCreateGraph"
      @register-cancel="registerCancel"
    ></modal-for-create-graph>
    <!-- グラフ作成完了モーダル -->
    <v-container>
      <v-layout row wrap>
        <v-flex xs12>
          <v-alert
            color="success"
            icon="check_circle"
            outline
            dismissible
            v-model="successAlert"
            >一時保存が完了しました。</v-alert
          >
        </v-flex>
        <v-flex xs12>
          <v-alert
            color="error"
            icon="warning"
            outline
            dismissible
            v-model="failedAlert"
            >グラフの保存に失敗しました。</v-alert
          >
        </v-flex>
      </v-layout>
      <v-layout row justify-center>
        <!-- グラフ作成完了モーダル -->
        <v-flex xs4>
          <v-btn
            class="black--text font-weight-bold"
            color="primary"
            dark
            @click="onButtonCreateModalClickHandler"
            >完成！</v-btn
          >
        </v-flex>
        <v-flex xs4>
          <v-btn
            class="black--text font-weight-bold"
            color="primary lighten-1"
            @click="onSaveButtonClickHandler"
            >一時保存</v-btn
          >
        </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>

<script lang="js">
import Vue from "vue";
import LabelDialog from "./LabelDialog";
import ColorPalette from "./ColorPalette.vue";
import ScrollLock from "./ScrollLock";
import ModalForCreateGraph from "./ModalForCreateGraph.vue";
import ErrorMessage from "@/components/parts/ApiErrorMessage.vue";

const RADIUS = 20;
const LABEL_HEIGHT = 100;
const LABEL_WIDTH = 100;
const DRAW_AREA_MARGIN = 5;
const PADDING = 10;

class Point {
  constructor({ x, y, xLabel, yLabel }) {
    this.x = x || 0;
    this.y = y || 0;
    this.xLabel = xLabel || "";
    this.yLabel = yLabel || "";
    this.xPath = "";
  }
}

class Degree {
  constructor({ y, label }) {
    this.y = y || 0;
    this.label = label || "";
    this.path = "";
  }
}

class ColorScheme {
  constructor({ id, base, accent, bg }) {
    this.id = id;
    this.base = base || "rgba(0, 0, 0, 1)";
    this.accent = accent || "rgba(0, 0, 0, 1)";
    this.bg = bg || "rgba(255, 255, 255, 1)";
  }
}

export default {
  components: {
    LabelDialog,
    ColorPalette,
    ScrollLock,
    ModalForCreateGraph,
    ErrorMessage
  },
  props: {
    title: {
      type: String,
      default: "タイトルなし"
    },

    /* 塗りつぶしの際に参照する水平線の位置(0.0〜1.0で指定)。喫水線。0の時は画面下部。 */
    waterline: { type: Number, default: 0.4 },

    /* 初期データ */
    initialDataList: { type: Array, default: () => [] },
    initialDegreeList: { type: Array, default: () => [] },
  },
  data() {
    return {
      BASE_SIZE: 400,

      /* svgサイズ */
      size: Math.min(window.innerWidth, window.innerHeight),

      /* 基底の点と軸データ */
      rawList: [], // Array<Point>

      /* 基底の目盛りデータ */
      degreeList: [], // Array<Degree>

      /* 点の前後での曲率。0で最も鋭くなる。 */
      radius: RADIUS,

      padding: PADDING,

      edit_index: 0,
      touch_pos: { x: -1, y: -1 },
      edit: { now: false, data: { x: -1, y: -1 } },
      add: { now: false, data: {x: -1, y: -1} },

      label_edit_flg: true,

      /* 編集中の軸ラベルまたは目盛りラベル */
      label: { x: -1, y: -1, axis: "", text: "" },
      degree: { text: "", y: -1 },

      nowColor: new ColorScheme({}),

      isExist: -1,
      interval_time: 0,

      /* xml書き出しモード:レンダリング時に参照 */
      exportingToXml: {
        /* マスター。true: 有効 */
        enabled: false,
        /* オプション。true: グラフ部分だけ切り抜く */
        cropGraph: false,
      },

      /* 'bezier': 曲線, 'lines': 折れ線 */
      mode: "bezier",

      /* グラフを固定する(編集できないようにする)かどうかのフラグ */
      flgLockGraph: true,

      showTagDialog: false,
      graph_id: null,
      base_graph_id: null,
      graph_title: 'タイトルなし',
      graph_tags: [],
      secretFlg: null,
      colorId: 1,

      /* 一時保存時のダイアログの表示フラグ */
      successAlert: false,
      failedAlert: false,

      /* グラフ描画タイプ */
      drawType: "new"
    };
  },
  watch: {
    title: function() {
      this.graph_title = this.title;
    },
    drawType: function() {
      this.setGraph();
    }
  },
  created() {
    const { waterlineY, initialDataList, initialDegreeList } = this;
    /* 点と軸 */
    this.rawList = (initialDataList || []).map(d => new Point(...d))
      .concat([
        new Point({ x: this.labelWidth + this.padding, y: waterlineY }),
        new Point({ x: this.size * this.magnify - this.padding, y: waterlineY }),
      ])
      .sort((a, b) => (a.x < b.x) ? -1 : 1)
      .filter((d, i, list) => i === list.findIndex(e => e.x === d.x));

    /* 目盛り */
    this.degreeList = (initialDegreeList || []).map(d => new Degree(...d))
      .concat([
        new Degree({ y: 173})
      ]);
  },
  methods: {
    initColor(color) {
      this.nowColor = new ColorScheme(color);
    },
    reflectColor(color) {
      this.nowColor = new ColorScheme(color);
    },
    onBottomAxisLabelTouchMoveHandler() {
      this.label_edit_flg = false;
    },
    /**
     * 横軸ラベルタップ時の動作。ラベル編集ダイアログを表示する
     */
    onBottomAxisLabelTouchEndtHandler({ x, y, axis }) {
      if(!this.label_edit_flg) {
        this.label_edit_flg = true;
        return false;
      }

      const axisLabel = this.getAxis({ x });

      this.label.text = "";
      if(axisLabel) {
        this.label.text = axisLabel[axis+'Label'];
      }
      this.label.x = x;
      this.label.y = y;
      this.label.axis = axis;
      this.$refs.graphLabelDialog.open("axis", this.label.text);
    },
    onLabelTouchMoveHandler() {
      this.label_edit_flg = false;
    },
    /**
     * グラフ左の目盛りをタップしたときの動作。目盛りラベル編集ダイアログを表示する
     * @param {Number} y 目盛りのy軸
     */
    onLeftDegreeLabelTouchEndHandler(y) {
      if(!this.label_edit_flg) {
        this.label_edit_flg = true;
        return false;
      }
      const degreeLabel = this.getDegree({y});
      this.degree.text = "";
      if(degreeLabel) {
        this.degree.text = degreeLabel.label;
      }
      this.degree.y = y;
      this.$refs.graphLabelDialog.open("degree", this.degree.text);
    },
    /**
     * 軸と点のデータをx座標から検索して取得する
     * @return {Point}
     */
    getAxis({ x }) {
      return this.rawList.find(d => d.x < x + 15 && d.x > x - 15);
    },
    /**
     * 目盛りデータをy座標から検索して取得する
     * @return {Degree}
     */
    getDegree({ y }) {
      return this.degreeList.find(d => d.y < y +15 && d.y > y - 15);
    },
    /**
     * ラベル編集用メソッド。子コンポーネントのdialogから呼ぶ
     * @param {'axis'|'degree'} mode それぞれ横軸と縦軸に対応
     * @param {String} text ラベル用文字列
     */
    setLabel({ mode, text }) {
      switch (mode) {
      case "axis":
        this.setAxisLabel(text);
        break;
      case "degree":
        this.setDegreeLabel(text);
        break;
      }
    },
    /** 軸ラベルをtextへ変更する。変更中のラベルはthis.labelを参照して取得する */
    setAxisLabel(text) {
      const { x, y, axis } = this.label;
      const data = this.getAxis({ x });
      if(data) {
        data[`${axis}Label`] = text.trim();
      } else {
        this.rawList.push(new Point({ x: x, y: y, xLabel: text.trim()}));
      }
    },
    /** 目盛りラベルをtextへ変更する。変更中のラベルはthis.degreeを参照して取得する */
    setDegreeLabel(text) {
      const { y } = this.degree;
      const degree = this.getDegree({ y }) || new Degree({ y, label: "" });
      degree.label = text.trim();
      this.degreeList.push(degree);
      this.flushDegree();
    },
    /** 目盛りのラベルが空文字のもの、y座標について重複しているものを削除する */
    flushDegree() {
      const { degreeList } = this;

      const resultList = [];
      for (let i = 0; i < degreeList.length; i++) {
        const d = degreeList[i];
        if (d.label.trim().length === 0) {
          continue;
        }
        for (let j = 0; j < degreeList.length; j++) {
          const dd = degreeList[j];
          if (d.y !== dd.y) {
            continue;
          }
          if (i === j) {
            resultList.push(d);
          }
          break;
        }
      }

      this.degreeList = resultList;
    },
    /** ラベル消去用メソッド。子コンポーネントのdialogから呼ぶ */
    clearLabel({ mode }) {
      switch (mode) {
      case "axis":
        this.clearAxisLabel();
        break;
      case "degree":
        this.removeDegreeLabel();
        break;
      }
    },
    /** 編集中の軸ラベルを未設定の状態に戻す */
    clearAxisLabel() {
      const { x, y, axis } = this.label;
      const data = this.getAxis({ x });
      if(data) {
        data[`${axis}Label`] = "";
      }
    },
    /** 編集中の目盛りを削除する */
    removeDegreeLabel() {
      const { y } = this.degree;
      const degree = this.getDegree({ y });
      if (degree) {
        degree.label = "";
      }
      this.flushDegree();
    },
    /**
     * img要素のsrcにそのまま指定可能な、dataURL形式のsvg画像を出力する。
     * スタイルシートの内容はこの方法で巻き込めないため、本コンポーネントではstyle属性で直接タグに埋め込む。
     * 1. xml書き出しモードに切り替え、DOMの更新を待つ。
     * 2. その状態でdataURLを生成する。
     * 3. xml書き出しモードから出て、DOMの更新を待つ。
     *
     * @returns {Promise<string>} xml形式のsvg画像
     */
    async toXmlDataUrl(option = {}) {
      const { cropGraph = true } = option;

      /* xml書き出しモードへ切り替え、未設定のラベル等を非表示に */
      this.exportingToXml.enabled = true;
      await Vue.nextTick();
      this.exportingToXml.cropGraph = cropGraph;
      await Vue.nextTick();

      const dataScheme = "data:image/svg+xml;charset=utf8,";
      const mojo = "<?xml version=\"1.0\" encoding=\"utf-8\"?>";
      const svgElement = document.getElementById("svgGraph");

      const rawXmlString = new XMLSerializer().serializeToString(svgElement);
      const xmlString = encodeURIComponent(mojo + rawXmlString);
      const dataUrl = dataScheme + xmlString;

      this.exportingToXml.enabled = false;
      this.exportingToXml.cropGraph = false;
      await Vue.nextTick();

      return dataUrl;
    },
    getGraphVertexes(rawList) {
      const result = [];
      for(const key in rawList) {
        result.push({
          display_num: parseInt(key) + 1,
          x_coordinate: rawList[key].x,
          y_coordinate: rawList[key].y,
          label_name: rawList[key].xLabel
        });
      }

      return result;
    },
    getVerticalLavel(degreeList) {
      const result = [];
      for(const key in degreeList) {
        result.push({
          coordinate_type: "Y",
          coordinate: degreeList[key].y,
          label_name: degreeList[key].label
        });
      }

      return result;
    },
    /** グラフをXMLに変換してDBに登録する。画面遷移はしない */
    async onSaveButtonClickHandler() {
      const payload = await this.getPayload();

      if(this.drawType == "edit") {
        this.saveEditGraph(payload);
      } else {
        if (this.base_graph_id && this.drawType == "template") {
          payload.template_graph_id = this.base_graph_id;
        }
        this.saveNewGraph(payload);
      }
    },
    saveNewGraph(payload) {
      this.$http
        .post(`/v1/graphs/`,
          payload,
          {
            headers: {
              'Authorization': 'Bearer ' + this.$store.state.login.auth_token
            }
          },
          {
            withCredentials: true
          }
        )
        .then(res => {
          this.successAlert = true;
          this.drawType = "edit";
          this.$router.push({name: 'create', query:{ id: res.data.graph_id }});
        })
        .catch(error =>{
          this.failedAlert = true;
          this.$refs.errorDialog.setMessage(
            "グラフの登録に失敗しました。詳細：[" +
              error.response.status +
              "] " +
              error.response.statusText
          );
        });
    },
    saveEditGraph(payload) {
      const EP = '/v1/graphs/' + this.base_graph_id;
      this.$http
        .put(EP, payload, {
          headers: {
            'Authorization': 'Bearer ' + this.$store.state.login.auth_token
          }
        },
        {
          withCredentials: true
        }
        )
        .then(res => {
          this.successAlert = true;
        })
        .catch(error =>{
          this.failedAlert = true;
          this.$refs.errorDialog.setMessage(
            "グラフの更新に失敗しました。詳細：[" +
              error.response.status +
              "] " +
              error.response.statusText
          );
        });
    },
    // Mouse
    onSvgMouseDownHandler(e) {
      const pos = this.getMousePos(e);

      // 既存の頂点をタップしたら削除する
      this.isExist = this.isExistPoint(this.rawList, pos.x, pos.y);
      if(this.isExist !== -1) {
        this.touch_pos = pos;
        this.deletePoint(e, pos);
      }

      if (this.isOutbound(pos)) {
        this.stopDelete();
        return false;
      }
      this.showGhostPoint(pos);
      this.setPrevent(e);
    },
    onSvgMouseMoveHandler(e) {
      const pos =this.getMousePos(e);
      if(this.isExist !== -1) {
        if((pos.x > this.touch_pos.x + 15 || pos.x < this.touch_pos.x - 15) || (pos.y < this.touch_pos.y - 15 || pos.y > this.touch_pos.y + 15)) {
          this.stopDelete();
        }
      }
      if (!this.add.now && !this.edit.now) {
        return false;
      }

      this.showGhostPoint(pos);
      this.setPrevent(e);
      return false;
    },
    onSvgMouseUpHandler() {
      if(this.isExist !== -1) {
        this.stopDelete();
      }
      this.applyGhostPoint();
      this.hideGhostPoint();
      this.isExist = -1;
    },
    deletePoint(e, pos) {
      // 1秒長押ししたら頂点を削除する
      document.interval = setInterval(() => {
        this.interval_time += 100;
        if(this.interval_time == 500) {
          const hasRemoved = this.onPointTouch(pos.x);
          if (hasRemoved) {
            e.stopPropagation();
            this.hideGhostPoint();
          }
        } else if(this.interval_time > 500) {
          this.stopDelete();
        }
      }, 100);
      this.setPrevent(e);
    },
    stopDelete() {
      this.interval_time = 0;
      clearInterval(document.interval);
    },
    // Touch
    onSvgTouchStartHandler(e) {
      // タッチした座標を取得します
      const pos = this.getPos(e);

      // 既存の頂点をタップしたら削除する
      this.isExist = this.isExistPoint(this.rawList, pos.x, pos.y);
      if(this.isExist !== -1) {
        this.touch_pos = pos;
        this.deletePoint(e, pos);
      }

      // タッチされた場所がグラフ描画エリア外だったら処理を中断します
      if (this.isOutbound(pos)) {
        this.stopDelete();
        return false;
      }
      // 設置予定の頂点を描画します
      this.showGhostPoint(pos);
      this.setPrevent(e);
    },
    onSvgTouchMoveHandler(e) {
      const pos = this.getPos(e);
      if(this.isExist !== -1) {
        if((pos.x > this.touch_pos.x + 15 || pos.x < this.touch_pos.x - 15) || (pos.y < this.touch_pos.y - 15 || pos.y > this.touch_pos.y + 15)) {
          this.stopDelete();
        }
      }
      // 編集中でなければ処理を中断します
      if (!this.add.now && !this.edit.now) {
        return false;
      }

      // 動かすたびに座標を更新して頂点を移動させます
      this.showGhostPoint(pos);
      this.setPrevent(e);
      return false;
    },
    onSvgTouchEndHandler(e) {
      if(this.isExist !== -1) {
        this.stopDelete();
      }
      this.applyGhostPoint();
      this.hideGhostPoint();
      this.setPrevent(e);

      this.isExist = -1;
    },
    setPrevent(e) {
      if(e.cancelable && !this.flgLockGraph) {
        e.preventDefault();
      }
    },
    onBottomLabelTouchEndHandler(e) {
      const { x } = this.getPos(e);

      let touchX = x + (this.padding + this.labelWidth);
      const first_label = this.padding + this.labelWidth;
      const end_label = this.size * this.magnify - this.padding;
      if(touchX < first_label + 15) {
        touchX = first_label;
      }
      if(touchX > end_label - 15) {
        touchX = end_label;
      }

      this.onBottomAxisLabelTouchEndtHandler({x: touchX, y: this.waterlineY, axis: "x"});
    },
    onBottomLabelMouseUpHandler(e) {
      const { x } = this.getMousePos(e);

      let touchX = x + (this.padding + this.labelWidth);
      const first_label = this.padding + this.labelWidth;
      const end_label = this.size * this.magnify - this.padding;
      if(touchX < first_label + 15) {
        touchX = first_label;
      }
      if(touchX > end_label - 15) {
        touchX = end_label;
      }

      this.onBottomAxisLabelTouchEndtHandler({x: touchX, y: this.waterlineY, axis: "x"});
    },
    onLeftLabelTouchEndHandler(e) {
      /* svgと左上基準点が同じ */
      const { y } = this.getPos(e);
      const height = Math.floor(this.innerSize * this.magnify - this.labelHeight);
      let label_y = y;

      if(y <= this.padding) {
        label_y += this.padding;
      }
      if(y >= (height - 5)) {
        label_y = this.padding + height;
      }

      this.onLeftDegreeLabelTouchEndHandler(label_y);
    },
    onLeftLabelMouseUpHandler(e) {
      const { y } = this.getMousePos(e);
      const height = Math.floor(this.innerSize * this.magnify - this.labelHeight);
      let label_y = y;

      if(y < this.padding) {
        label_y += this.padding;
      }
      if(y >= (height - 5)) {
        label_y = this.padding + height;
      }

      this.onLeftDegreeLabelTouchEndHandler(label_y);
    },
    // Touch and Mouse
    onPointTouch(x) {
      if(this.flgLockGraph) {
        return false;
      }
      const hasRemoved = this.removePoint(x);
      return hasRemoved;
    },
    onResetButtonClickHandler() {
      this.resetData();
    },
    /**
     * グラフをxml形式のDataUrlへ変換してstoreへコミット、完了ダイアログを開く
     * @returns {Promise<void>}
     */
    async onButtonCreateModalClickHandler() {
      const payload = await this.getPayload();
      if(this.drawType == "edit") {
        this.registerEditGraph(payload);
      } else {
        if (this.base_graph_id && this.drawType == "template") {
          payload.template_graph_id = this.base_graph_id;
        }
        this.registerNewGraph(payload);
      }
    },
    registerNewGraph(payload) {
      this.$http
        .post(`/v1/graphs/`,
          payload,
          {
            headers: {
              'Authorization': 'Bearer ' + this.$store.state.login.auth_token
            }
          },
          {
            withCredentials: true
          }
        )
        .then((res) => {
          this.graph_id = res.data.graph_id;
          this.$refs.modalForCreateGraph.show();
        })
        .catch(error =>{
          this.failedAlert = true;
          this.$refs.errorDialog.setMessage(
            "グラフの登録に失敗しました。詳細：[" +
              error.response.status +
              "] " +
              error.response.statusText
          );
        });
    },
    registerEditGraph(payload) {
      const EP = '/v1/graphs/' + this.base_graph_id;
      this.$http.put(EP, payload, {
        headers: {
          'Authorization': 'Bearer ' + this.$store.state.login.auth_token
        }
      },
      {
        withCredentials: true
      })
        .then((res) => {
          this.graph_id = res.data.graph_id;
          this.$refs.modalForCreateGraph.show();
        })
        .catch(error => {
          this.failedAlert = true;
          this.$refs.errorDialog.setMessage(
            "グラフの更新に失敗しました。詳細：[" +
              error.response.status +
              "] " +
              error.response.statusText
          );
        });
    },
    registerCancel() {
      this.drawType = "edit";
      this.$router.push({name: 'create', query:{ id: this.graph_id }});
    },
    getLineType() {
      const line_type = this.mode === "line" ? 2 : 1;
      return line_type;
    },
    async getPayload() {
      const graphImageUrl = await this.toXmlDataUrl({cropGraph: false});
      const graphImageNoLabelUrl = await this.toXmlDataUrl({cropGraph: true});

      const line_type = this.getLineType();

      const result = {
        user_id: Number(this.$store.state.login.user_id),
        graph_title: this.graph_title,
        color_id: this.nowColor.id,
        line_type: line_type + '',
        xml_data: graphImageUrl,
        xml_data_no_labels: graphImageNoLabelUrl,
        graph_vertexes: this.getGraphVertexes(this.rawList),
        vertical_label: this.getVerticalLavel(this.degreeList)
      };

      return result;
    },
    showGhostPoint({ x, y }) {
      if(this.flgLockGraph) {
        return false;
      }
      if(this.isExist !== -1) {
        this.edit_index = this.isExist;
        this.edit = { now: true, data: { x, y }};
      } else {
        this.add = { now: true, data: { x, y } };
      }
    },
    hideGhostPoint() {
      this.edit.now = false;
      this.add.now = false;
    },
    /* 既存の頂点とx座標がかぶるかどうか */
    isExistPointX(list, x) {
      const isExistPoint = list.findIndex(d => d.x < x + 15 && d.x > x - 15);
      return isExistPoint;
    },
    isExistPoint(list, x, y) {
      const isExistPointX = list.findIndex(d => d.x < x + 15 && d.x > x - 15);

      if(isExistPointX !== -1){
        const existY = list[isExistPointX].y;
        if(existY > y - 15 && existY < y + 15) {
          return isExistPointX;
        }
      }

      return -1;
    },
    applyGhostPoint() {
      if(!this.add.now && !this.edit.now) {
        return false;
      }
      if(this.add.now) {
        return this.applyAddGhostPoint();
      }
      if(this.edit.now) {
        return this.applyEditGhostPoint();
      }
    },
    applyAddGhostPoint() {
      const { x, y } = this.lockPosInBoundary(this.add.data);
      const index = this.isExistPointX(this.rawList, x);

      /* 同じx座標、縦に並んだら上書き。それ以外は追加 */
      if (index !== -1) {
        this.rawList.splice(index, 1, new Point({ x, y }));
      } else {
        this.rawList.push(new Point({ x, y }));
      }

      this.rawList.sort((a, b) => (a.x < b.x) ? -1 : 1);

      return (index !== -1) ? "replace" : "add";
    },
    applyEditGhostPoint() {
      const { x, y } = this.lockPosInBoundary(this.edit.data);
      const index = this.isExistPointX(this.rawList, x);

      if(index !== -1) {
        const xLabel = this.rawList[index].xLabel;
        this.rawList.splice(index, 1, new Point({ x, y, xLabel}));
      } else {
        this.rawList[this.edit_index].x = x;
        this.rawList[this.edit_index].y = y;
      }

      this.rawList.sort((a, b) => (a.x < b.x) ? -1 : 1);

      return "replace";
    },
    removePoint(x) {
      const index = this.isExistPointX(this.rawList, x);
      if (index !== -1) {
        this.rawList.splice(index, 1);
      }
      return index !== -1;
    },
    resetData() {
      const { labelWidth, waterlineY, padding, size, magnify } = this;
      this.rawList.splice(0, this.rawList.length);
      this.rawList.push(
        new Point({ x: labelWidth + padding, y: waterlineY }),
        new Point({ x: size * magnify - padding, y: waterlineY }),
      );
    },
    /**
     * TouchEvent対象のDOMについて、DOMの左上からの座標を返す
     * @param {TouchEvent} e
     * @returns {{x: number, y: number}}
     */
    getPos(e) {
      const { changedTouches, currentTarget } = e;
      const { pageX, pageY } = changedTouches[0];
      const { left, top } = currentTarget.getBoundingClientRect();
      const { pageXOffset, pageYOffset } = window;
      let x = pageX - (pageXOffset + left);
      x = (x > this.size) ? this.size : (x < 0 ? 0 : x);
      const y = pageY - (pageYOffset + top);
      const { magnify } = this;

      return { x: Math.floor(x * magnify), y: Math.floor(y * magnify) };
    },
    /**
     * GetPos()のマウスイベント版
     * @param {MouseEvent} e
     * @returns {{x: number, y: number}}
     */
    getMousePos(e) {
      const { pageX, pageY } = e;
      const { left, top } = e.currentTarget.getBoundingClientRect();
      const { pageXOffset, pageYOffset } = window;

      let x = pageX - (pageXOffset + left);
      x = (x > this.size) ? this.size : (x < 0 ? 0 : x);
      const y = pageY - (pageYOffset + top);
      const { magnify } = this;

      return { x: Math.floor(x * magnify), y: Math.floor(y * magnify) };
    },
    /**
     * グラフの描画範囲に入っていない場合はtrue
     * @return {boolean}
     */
    isOutbound({ x, y }) {
      const { labelWidth, labelHeight, padding, size, magnify } = this;
      const left = x < labelWidth;
      const right = x > size * magnify - padding;
      const above = y < 0;
      const below = (y > size * magnify - labelHeight);
      return left
        || right
        || above
        || below;
    },
    /**
     * 描画範囲内に補正したpos(xy座標)を返す
     * @param {{x:number}, {y:number}}
     */
    lockPosInBoundary({ x, y }) {
      const { labelWidth, labelHeight, padding: p, size, magnify } = this;
      x = (x < (labelWidth + p)) ? (labelWidth + p) : (x > size * magnify - p ? size * magnify - p : x);
      y = y < p ? p : ((y > size * magnify - labelHeight - p) ? size * magnify - labelHeight - p : y);

      return { x, y };
    },
    xAxisPath(data) {
      const { size, magnify, labelHeight } = this;
      const { x, y } = data;
      const path = `M${x} ${y} v ${(size * magnify - labelHeight) - y}`;
      return path;
    },
    yAxisPath(data) {
      const { labelWidth } = this;
      const { x, y } = data;
      const path = `M${labelWidth} ${y} h ${x - labelWidth}`;
      return path;
    },
    /**
     * 直線を描画するためのパスを作成する
     * @param {Point[]} dataList
     * @returns {string}
     */
    straightLinePath(dataList) {
      if (!dataList || dataList.length === 0) {
        return "";
      }
      const { padding: p } = this;
      const pathList = [];
      const height = this.innerSize * this.magnify - this.labelHeight;

      pathList.push(`M${dataList[0].x} ${dataList[0].y}`);

      for (let i = 1; i < dataList.length; i++) {
        const { x, y } = dataList[i];
        pathList.push(`L ${x} ${y}`);
      }

      /* 喫水線の両端、塗りつぶし用。 */
      pathList.push(`L ${dataList[dataList.length - 1].x} ${p + height}`);
      pathList.push(`L ${dataList[0].x} ${p + height}`);

      pathList.push("Z");

      const path = pathList.join(" ");

      return path;
    },
    /**
     * 曲線を描画するためのパスを作成する
     * @param {Point[]} dataList
     * @returns {string}
     */
    bezierPath(dataList) {
      if (!dataList || dataList.length === 0) {
        return "";
      }
      const { radius: R, padding: p } = this;
      const pathList = [];
      const height = this.innerSize * this.magnify - this.labelHeight;

      for (let i = 0; i < dataList.length; i++) {
        const { x, y } = dataList[i];

        /* 始点、中間、終点を判定 */
        const type = (i === 0) ? "start" : (i === dataList.length - 1 ? "end" : "point");

        switch (type) {
        case "start": {
          pathList.push(`M${x} ${y}`);
          break;
        }
        case "end": {
          pathList.push(`S${x - R} ${y}, ${x} ${y}`);
          break;
        }
        case "point": {
          const { x: px, y: py } = dataList[i - 1];
          const { y: fy } = dataList[i + 1];

          if (i === 1) {
            pathList.push(`C ${px + R} ${py},`);
          } else {
            pathList.push(`S`);
          }

          const pointType = (y - py <= 0 && fy - y >= 0) ? "top" : (y - py >= 0 && fy - y <= 0 ? "bottom" : "slope");
          switch (pointType) {
          case "bottom": {
            /* 下に凸: 接線は並行 */
            pathList.push(`${x - R} ${y}, ${x} ${y}`);
            break;
          }
          case "top": {
            /* 上に凸: 接線は並行 */
            pathList.push(`${x - R} ${y}, ${x} ${y}`);
            break;
          }
          case "slope": {
            /* 変曲点: 長さ0の接線を作成 */
            if (y - py > 0) {
              /* 下り勾配 */
              pathList.push(`${x} ${y}, ${x} ${y}`);
            } else {
              /* 上り勾配 */
              pathList.push(`${x} ${y}, ${x} ${y}`);
            }
            break;
          }
          }
          break;
        }
        }
      }

      /* 喫水線の両端、塗りつぶし用。 */
      pathList.push(`L ${dataList[dataList.length - 1].x} ${p + height}`);
      pathList.push(`L ${dataList[0].x} ${p + height}`);

      pathList.push("Z");
      const path = pathList.join(" ");
      return path;
    },
    writingStraightLine() {
      this.mode = "lines";
    },
    writingBezierCurve() {
      this.mode = "bezier";
    },
    scrollLock(lock_graph_flg) {
      if(lock_graph_flg == true) {
        this.flgLockGraph = false;
        this.$store.commit('setFullScreenFlg', true);
        document.addEventListener('touchmove', this.forcedTouchMoveHandler, { passive: false });
      } else {
        this.flgLockGraph = true;
        this.$store.commit('setFullScreenFlg', false);
        document.removeEventListener('touchmove', this.forcedTouchMoveHandler, { passive: false });
      }
    },
    /**
     * svg要素のstyleを返す
     * @return {string} css
     */
    svgStyles() {
      return `background-color: transparent;`;
    },
    /**
     * 目盛りおよび軸ラベルのstyleを返す
     * @param {'axis'|'degree'} axis
     * @param {Point|Degree} data
     * @return {string} css
     */
    textLabelStyles(axis, data) {
      const { exportingToXml } = this;
      const { enabled } = exportingToXml;
      const { xLabel = "" } = data;
      const { label = "" } = data;
      const labeled = (axis === "axis" && xLabel !== "")
        || (axis === "degree" && label !== "");
      return `
      font-family: Meiryo;
      font-size: 0.65rem;
      fill: ${labeled ? "dimgray" : (enabled ? "transparent" : "lightgray")}
      ${axis === "axis" ? `
        -webkit-writing-mode: vertical-rl;
        -ms-writing-mode: tb-rl;
        writing-mode: vertical-rl;` : ""}`;
    },
    /**
     * グラフ描画エリアのstyleを返す
     * @return {string} css
     */
    drawAreaRectStyles() {
      const { bg } = this.color;
      return `fill:${bg};`;
    },
    /**
     * グラフのstyleを返す
     * @return {string} css
     */
    graphPathStyles() {
      const { base } = this.color;

      return `
      stroke:${base};
      fill:${base};
      fill-opacity: 0.5;
      `;
    },
    /**
     * 編集中のグラフのstyleを返す
     * @return {string} css
     */
    ghostGraphPathStyles() {
      const { base, accent } = this.color;
      return `
      stroke:${accent};
      fill:${base};
      fill-opacity: 0.5;
      stroke-dasharray: 2;
      `;
    },
    /**
     * 塗りつぶし基準線のstyleを返す
     * @return {string} css
     */
    waterlinePathStyles() {
      const { base } = this.color;
      return `
      stroke:${base};
      fill:${base};
      stroke-dasharray: 2;
      `;
    },
    /**
     * x軸補助線のstyleを返す
     * @param {Point} axis
     * @return {string} css
     */
    xAxisPathStyles(axis) {
      const { base } = this.color;
      const { exportingToXml } = this;
      const { enabled } = exportingToXml;
      const { xLabel = "" } = axis;
      const labeled = (xLabel !== "");
      return `
      stroke:${base};
      stroke-opacity: ${labeled ? "0.5" : (enabled ? "0" : "0.5")};
      ${labeled ? "" : "stroke-dasharray: 3;"}
      `;
    },
    /**
     * y軸補助線のstyleを返す
     * @param {Degree} degree
     * @return {string} css
     */
    degreePathStyles(degree) {
      const { base } = this.color;
      return `
      stroke:${base};
      stroke-opacity: 0.5;
      stroke-dasharray: 3;
      `;
    },
    /**
     * 点のstyleを返す
     * @return {string} css
     */
    pointCircleStyles() {
      const { accent } = this.color;
      return `
      stroke:${accent};
      fill:${accent};
      `;
    },
    labelPath(degree) {
      const { y } = degree;
      const { labelWidth, size, magnify, padding } = this;

      return `M${labelWidth} ${y} h ${size * magnify - labelWidth - padding}`;
    },
    async setGraph() {
      const graph = await this.getGraph();
      this.base_graph_id = graph.graph_id;
      this.colorId = graph.color_id;
      this.graph_tags = this.setTags(graph.graph_tags);
      this.secretFlg = graph.secret_flg;  //ネタバレあり(False)
      this.$emit('setTitle', graph.graph_title);

      // 線種別をセットします
      this.mode = "bezier";
      if(graph.line_type == 2) {
        this.mode = "lines";
      }

      this.rawList = [];
      this.degreeList = [];
      // 頂点座標と横軸ラベルをセットします
      for(const key in graph.graph_vertexes) {
        this.rawList.push(
          new Point({
            x: graph.graph_vertexes[key].x_coordinate,
            y: graph.graph_vertexes[key].y_coordinate,
            xLabel: graph.graph_vertexes[key].label_name
          })
        );
      }
      // 縦軸ラベルをセットします
      for(const key in graph.vertical_label) {
        this.degreeList.push(
          new Degree({
            y: graph.vertical_label[key].coordinate,
            label: graph.vertical_label[key].label_name
          })
        );
      }
    },
    getGraph() {
      const apiUrl = "/v1/graphs/"+this.$route.query.id;
      const graph = [];
      return this.$http.get(apiUrl)
        .then(res => {
          for(const key in res.data) {
            graph[key] = res.data[key];
          }
          return graph;
        })
        .catch(error =>{
          this.$refs.errorDialog.setMessage(
            "グラフの取得に失敗しました。詳細：[" +
              error.response.status +
              "] " +
              error.response.statusText
          );
          return graph;
        });
    },
    setTags(tags) {
      const result = [];
      for(const key in tags) {
        result.push(tags[key].tag_name);
      }
      return result;
    },
    /**
     * イベント強制無効化処理
     */
    forcedTouchMoveHandler(e) {
      e.preventDefault();
    },
    addPoint(ghostList) {
      /* 描画範囲をはみ出していたら補正 */
      const { x, y } = this.lockPosInBoundary(this.add.data);

      const index = this.isExistPointX(ghostList, x);
      if (index === -1) {
        ghostList.push(new Point({ x, y }));
      } else {
        ghostList.splice(index, 1, new Point({ x, y }));
      }
      ghostList.sort((a, b) => (a.x < b.x) ? -1 : 1);
      return ghostList;
    },
    editPoint(ghostList) {
      /* 描画範囲をはみ出していたら補正 */
      const { x, y } = this.lockPosInBoundary(this.edit.data);

      const index = this.isExistPointX(ghostList, x);
      if(index !== -1) {
        ghostList.splice(index, 1, new Point({ x, y }));
      } else {
        ghostList[this.edit_index].x = x;
        ghostList[this.edit_index].y = y;
      }
      ghostList.sort((a, b) => (a.x < b.x ? -1 : 1));
      return ghostList;
    },
  },
  computed: {
    /**
     * カラーパレットで現在選択中のスタイルを返す
     * @return {ColorScheme}
     */
    color() {
      return new ColorScheme(this.nowColor);
    },
    waterlineY() {
      return Math.floor((this.innerSize - this.labelHeight - this.drawAreaMargin) * (1 - this.waterline) * this.magnify);
    },
    labelHeight() {
      return LABEL_HEIGHT;
    },
    labelWidth() {
      return LABEL_WIDTH;
    },
    drawAreaMargin() {
      return DRAW_AREA_MARGIN;
    },
    viewboxX() {
      return this.exportingToXml.cropGraph ? this.labelWidth : 0;
    },
    viewboxY() {
      return 0;
    },
    viewboxW() {
      return this.exportingToXml.cropGraph ? Math.floor((this.innerSize * this.magnify - this.labelWidth) + 2 * this.padding) : this.BASE_SIZE;
    },
    viewboxH() {
      return this.exportingToXml.cropGraph ? Math.floor((this.innerSize * this.magnify - this.labelHeight) + 2 * this.padding) : this.BASE_SIZE;
    },
    magnify() {
      return this.BASE_SIZE / this.size;
    },
    innerSize() {
      return this.size - this.padding * 2;
    },
    /**
     * 通常のグラフ描画用。rawListの射影。
     * @returns {{x: number, y: number}[]}
     */
    dataList() {
      const dataList =
        this.rawList
          .map(d => new Point({ x: d.x, y: d.y }))
          .sort((a, b) => (a.x < b.x) ? -1 : 1);
      return dataList;
    },
    /**
     * 編集中のグラフ描画用。操作中の点をrawListに加えたものの射影。
     * @returns {{x: number, y: number}[]}
     */
    ghostDataList() {
      if (!this.add.now && !this.edit.now) {
        return [];
      }
      const ghostList = this.rawList.map(d => new Point({ x: d.x, y: d.y }));

      if(this.add.now) {
        return this.addPoint(ghostList);
      }

      if(this.edit.now) {
        return this.editPoint(ghostList);
      }
    },
    /**
     * 補助軸描画用。
     * @return {Point[]}
     */
    axisDataList() {
      const axisList = this.rawList.map(d => {
        const { x, y, xLabel, yLabel } = d;
        const axis = new Point({ x, y, xLabel, yLabel, });
        axis.xPath = this.xAxisPath(d);
        axis.yPath = this.yAxisPath(d);

        return axis;
      });

      return axisList;
    },
    /**
     * 目盛り描画用。
     * @return {Degree[]}
     */
    degreeDataList() {
      const { degreeList } = this;
      return degreeList.map(d => {
        d.path = this.labelPath(d);
        return d;
      });
    },
    ghostGraphPath() {
      const { mode } = this;
      if (mode === "bezier") {
        return this.bezierPath(this.ghostDataList);
      } else if (mode === "lines") {
        return this.straightLinePath(this.ghostDataList);
      }
      return "";
    },
    graphPath() {
      const { mode } = this;
      if (mode === "bezier") {
        return this.bezierPath(this.dataList);
      } else if (mode === "lines") {
        return this.straightLinePath(this.dataList);
      }
      return "";
    },
    /**
     * 喫水線(塗りつぶし時の基準線)
     * @returns {string}
     */
    waterlinePath() {
      const { labelWidth, waterlineY, size, magnify, padding } = this;
      const path = `M${labelWidth} ${waterlineY} L ${size * magnify - padding} ${waterlineY}`;
      return path;
    }
  },
  mounted() {
    this.drawType = this.$store.state.drawType;
    // 描画タイプを渡したので初期化する
    this.$store.commit('setGraphType', 'new');

    if(this.drawType != "new") {
      this.setGraph();
    }
  }
};
</script>
